import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, CheckCircle2, ChevronDown, X } from "lucide-react";
import { supabase } from "../supabaseClient";
import { normalizeProjectStatus } from "../utils/projectStatus";
import { traceAsync } from "../utils/perfTrace";

const PANEL =
  "rounded-2xl border border-white/15 bg-white/10 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.08)]";

const SMS_CONSENT_COPY =
  "Yes, I want to receive automated text message notifications from Strobe Aviation about my broker request submissions and project status updates. Message frequency varies based on account activity. Message and data rates may apply. Reply STOP to unsubscribe. For help, contact support@strobeaviation.com. Consent is not required to use the portal.";

const normalizeStatus = (status) => normalizeProjectStatus(status);

export default function BrokerNotifications({ className = "", panelClass = PANEL }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [notice, setNotice] = useState(null);

  const [enabledEmail, setEnabledEmail] = useState(false);
  const [enabledSMS, setEnabledSMS] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [listening, setListening] = useState(false);
  const channelRef = useRef(null);
  const intakeTailByIdRef = useRef(new Map());
  const noticeTimerRef = useRef(null);

  const bellIsOn = enabledEmail || enabledSMS;

  const showNotice = useCallback((nextNotice) => {
    setNotice(nextNotice);
    if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = window.setTimeout(() => setNotice(null), 8000);
  }, []);

  const stopRealtime = useCallback(() => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    setListening(false);
  }, []);

  const resolveBrokerAccountId = useCallback(async (uid) => {
    const { data, error } = await supabase
      .from("broker_account_members")
      .select("broker_account_id")
      .eq("user_id", uid)
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return data?.broker_account_id || "";
  }, []);

  const loadAccountIntakeMap = useCallback(async (brokerAccountId) => {
    const { data, error } = await supabase
      .from("broker_intake")
      .select("id, tail_number")
      .eq("broker_account_id", brokerAccountId);

    if (error) throw error;

    intakeTailByIdRef.current = new Map(
      (data || []).map((row) => [row.id, row.tail_number || "a project"])
    );
  }, []);

  const startRealtimeForMyProjects = useCallback(
    async (uid) => {
      stopRealtime();

      try {
        const brokerAccountId = await traceAsync(
          "notifications.broker.resolveAccount",
          () => resolveBrokerAccountId(uid),
          {}
        );
        if (!brokerAccountId) {
          setMessage("Notifications are saved, but broker account access could not be confirmed.");
          return;
        }

        await traceAsync(
          "notifications.broker.intakeMap",
          () => loadAccountIntakeMap(brokerAccountId),
          {}
        );

        const channel = supabase
          .channel(`broker_project_status_updates_${brokerAccountId}`)
          .on(
            "postgres_changes",
            {
              event: "UPDATE",
              schema: "public",
              table: "project_status",
            },
            (payload) => {
              const intakeId = payload.new?.intake_id;
              const tail = intakeTailByIdRef.current.get(intakeId);
              if (!tail) return;

              const oldStatus = normalizeStatus(payload.old?.current_status);
              const newStatus = normalizeStatus(payload.new?.current_status);
              if (!newStatus || oldStatus === newStatus) return;

              showNotice({
                tail,
                oldStatus: oldStatus || "Previous status",
                newStatus,
              });
            }
          )
          .subscribe((status) => {
            setListening(status === "SUBSCRIBED");
            if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
              setMessage("Notifications are saved, but live updates are not connected.");
            }
          });

        channelRef.current = channel;
      } catch (error) {
        console.error("Failed to load broker intakes for notifications:", error);
        setMessage("Notifications are saved, but project listening could not start.");
        return;
      }
    },
    [loadAccountIntakeMap, resolveBrokerAccountId, showNotice, stopRealtime]
  );

  useEffect(() => {
    let alive = true;

    (async () => {
      const { data: authData, error: authError } = await traceAsync(
        "notifications.broker.getUser",
        () => supabase.auth.getUser(),
        {}
      );
      const user = authData?.user;
      if (!alive) return;

      if (authError || !user) {
        setLoading(false);
        return;
      }

      const { data, error } = await traceAsync(
        "notifications.broker.prefs",
        () => supabase
          .from("broker_notification_prefs")
          .select("email, phone, wants_email, wants_sms")
          .eq("user_id", user.id)
          .maybeSingle(),
        {}
      );

      if (!alive) return;

      if (error) {
        console.error("Failed to load notification preferences:", error);
        setMessage("Could not load notification preferences.");
        setLoading(false);
        return;
      }

      const wantsEmail = data ? !!data.wants_email : true;
      const wantsSMS = !!data?.wants_sms;
      setEnabledEmail(wantsEmail);
      setEnabledSMS(wantsSMS);
      setEmail(data?.email || user.email || "");
      setPhone(data?.phone || "");
      setLoading(false);

      if (wantsEmail || wantsSMS) {
        await startRealtimeForMyProjects(user.id);
      }
    })();

    return () => {
      alive = false;
      stopRealtime();
      if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);
    };
  }, [startRealtimeForMyProjects, stopRealtime]);

  async function savePrefs() {
    setMessage("");

    const wants_email = enabledEmail;
    const wants_sms = enabledSMS;
    const cleanEmail = email.trim();
    const cleanPhone = phone.trim();

    if (!wants_email && !wants_sms) {
      setMessage("Choose email, SMS, or both before saving.");
      return;
    }
    if (wants_email && !cleanEmail) {
      setMessage("Enter an email address for email notifications.");
      return;
    }
    if (wants_sms && !cleanPhone) {
      setMessage("Enter a mobile phone number for SMS notifications.");
      return;
    }

    setSaving(true);
    const { data: authData, error: authError } = await supabase.auth.getUser();
    const user = authData?.user;

    if (authError || !user) {
      setSaving(false);
      setMessage("You must be signed in to save notification preferences.");
      return;
    }

    const { error } = await supabase
      .from("broker_notification_prefs")
      .upsert(
        {
          user_id: user.id,
          email: cleanEmail || null,
          phone: cleanPhone || null,
          wants_email,
          wants_sms,
        },
        { onConflict: "user_id" }
      );

    if (error) {
      console.error("Failed to save notification preferences:", error);
      setSaving(false);
      setMessage("Could not save notification preferences.");
      return;
    }

    await startRealtimeForMyProjects(user.id);
    setSaving(false);
    setMessage("Notification preferences saved.");
    setOpen(false);
  }

  const statusText = useMemo(() => {
    if (loading) return "Loading notification settings";
    if (!bellIsOn) return "Notifications off";
    if (listening) return "Notifications enabled";
    return "Notifications saved; live updates connecting";
  }, [bellIsOn, listening, loading]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`${panelClass} ${className} p-2 hover:bg-white/20 transition inline-flex items-center justify-center relative`}
        aria-label="Notification settings"
        title={statusText}
        type="button"
      >
        <Bell className={`w-5 h-5 ${bellIsOn ? "text-[#EB040D]" : "text-[#1A1A1A]"}`} />
        {bellIsOn && listening && (
          <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[#EB040D]" />
        )}
      </button>

      {notice && (
        <div className="fixed right-4 top-4 z-50 w-[min(92vw,360px)] rounded-xl border border-black/10 bg-white p-4 shadow-2xl">
          <button
            type="button"
            onClick={() => setNotice(null)}
            className="absolute right-2 top-2 rounded p-1 text-black/50 hover:bg-black/5 hover:text-black"
            aria-label="Dismiss notification"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex gap-3 pr-5">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#EB040D]" />
            <div>
              <p className="text-sm font-semibold text-[#1A1A1A]">Project status updated</p>
              <p className="mt-1 text-sm text-black/65">
                {notice.tail}: {notice.oldStatus} to {notice.newStatus}
              </p>
            </div>
          </div>
        </div>
      )}

      {open && createPortal(
        <div className="fixed inset-0 z-[1000] overflow-y-auto p-3 sm:p-6">
          <div className="fixed inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative mx-auto flex max-h-[calc(100dvh-1.5rem)] w-full max-w-[640px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl sm:max-h-[calc(100dvh-3rem)]">
            <button
              onClick={() => setOpen(false)}
              className="absolute right-4 top-4 z-10 rounded-lg bg-black/[0.03] p-2 text-black/70 hover:bg-black/[0.06] hover:text-black"
              aria-label="Close"
              type="button"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="border-b border-black/10 px-5 py-4 sm:px-6">
              <h2 className="text-lg font-semibold">Notification Preferences</h2>
              <p className="mt-1 max-w-xl text-sm text-black/60">
                Choose how Strobe Aviation should notify you when your broker requests change status.
              </p>
            </div>

            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium">
                    <input
                      type="checkbox"
                      checked={enabledEmail}
                      onChange={(e) => setEnabledEmail(e.target.checked)}
                      className="h-4 w-4 accent-[#EB040D]"
                    />
                    Email notifications
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full rounded-lg border border-black/10 px-3 py-2.5 text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium">
                    <input
                      type="checkbox"
                      checked={enabledSMS}
                      onChange={(e) => setEnabledSMS(e.target.checked)}
                      className="h-4 w-4 accent-[#EB040D]"
                    />
                    Mobile phone (SMS)
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 555 123 4567"
                    className="w-full rounded-lg border border-black/10 px-3 py-2.5 text-sm"
                  />
                </div>
              </div>

              <details className="group rounded-xl border border-black/10 bg-black/[0.015] text-xs leading-relaxed text-black/65">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-medium text-black/75 [&::-webkit-details-marker]:hidden">
                  <span>SMS consent details</span>
                  <ChevronDown className="h-4 w-4 shrink-0 text-black/45 transition group-open:rotate-180" />
                </summary>
                <div className="border-t border-black/10 px-4 pb-4 pt-3">
                  <p>{SMS_CONSENT_COPY}</p>
                  <p className="mt-3">
                    <span className="font-medium text-black/70">Message frequency:</span> varies by account activity.
                    <span className="ml-2 font-medium text-black/70">Standard rates:</span> message and data rates may apply.
                  </p>
                  <p className="mt-2">
                    <a className="text-[#EB040D] underline underline-offset-2" href="/terms" target="_blank" rel="noreferrer">
                      Terms of Service
                    </a>
                    <span className="mx-1 text-black/30">|</span>
                    <a className="text-[#EB040D] underline underline-offset-2" href="/privacy" target="_blank" rel="noreferrer">
                      Privacy Policy
                    </a>
                  </p>
                </div>
              </details>

              {message && (
                <p className="rounded-md bg-black/[0.04] px-3 py-2 text-xs text-black/70">
                  {message}
                </p>
              )}
            </div>

            <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-black/10 bg-white px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
              <button
                onClick={() => setOpen(false)}
                className="rounded-lg border border-black/10 px-4 py-2.5 text-sm hover:bg-black/[0.04]"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={savePrefs}
                disabled={loading || saving}
                className="rounded-lg bg-[#EB040D] px-4 py-2.5 text-sm text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                type="button"
              >
                {saving ? "Saving..." : bellIsOn ? "Save" : "Enable Notifications"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
